import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// New banner image
const BANNER_IMAGE = "https://customer-assets.emergentagent.com/job_regulated-payments/artifacts/tfcf0b71_bs_banner_latest.png";

export const HeroSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  const stripeOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const stripeScaleX = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);
  const stripeY = useTransform(scrollYProgress, [0, 0.5], [0, -50]);

  const trustIndicators = [
    'Canadian MSB',
    'EU PI Agent',
    'UK API Agent'
  ];

  return (
    <section 
      ref={sectionRef}
      id="home" 
      className="relative min-h-[85vh] bg-white pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated Black Stripe - full width, thicker */}
      <motion.div
        className="absolute left-0 right-0 h-6 lg:h-8 black-stripe z-10 pointer-events-none hidden lg:block"
        style={{
          bottom: '8%',
          opacity: stripeOpacity,
          scaleX: stripeScaleX,
          y: stripeY,
          transformOrigin: 'left center',
        }}
        data-testid="hero-black-stripe"
      />

      <div className="section-container relative z-20 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-xl">
            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-2 md:gap-3 mb-6"
              data-testid="hero-trust-indicators"
            >
              {trustIndicators.map((indicator, index) => (
                <span
                  key={indicator}
                  className="inline-flex items-center text-[11px] md:text-sm font-semibold tracking-[0.12em] uppercase text-[#6B7280]"
                >
                  {indicator}
                  {index < trustIndicators.length - 1 && (
                    <span className="mx-2 md:mx-3 w-1 h-1 bg-[#6B7280] rounded-full" />
                  )}
                </span>
              ))}
            </motion.div>

            {/* Mobile/Tablet: Single headline block */}
            <div className="lg:hidden">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="heading-xl text-[2.5rem] md:text-5xl leading-[1.08] text-[#1E3A5F] mb-6"
                data-testid="hero-headline-mobile"
              >
                Payment Infrastructure.
                <br />
                Built for Scale.
                <br />
                Designed for Trust.
              </motion.h1>
            </div>

            {/* Desktop: Headlines */}
            <div className="hidden lg:block">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="heading-xl text-4xl xl:text-5xl 2xl:text-6xl text-[#1E3A5F] mb-2"
                data-testid="hero-headline"
              >
                Payment Infrastructure.
              </motion.h1>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="heading-xl text-4xl xl:text-5xl 2xl:text-6xl text-[#1E3A5F] mb-2"
                data-testid="hero-headline-2"
              >
                Built for Scale.
              </motion.h1>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="heading-xl text-4xl xl:text-5xl 2xl:text-6xl text-[#1E3A5F] mb-6"
                data-testid="hero-headline-3"
              >
                Designed for Trust.
              </motion.h1>
            </div>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="text-base md:text-lg text-[#6B7280] leading-relaxed max-w-lg mb-8"
              data-testid="hero-subheadline"
            >
              A regulated payment institution delivering reliable, transparent and compliant 
              payment infrastructure for corporate and retail clients worldwide.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap gap-4"
              data-testid="hero-ctas"
            >
              <a
                href="mailto:payments@easysendservices.com?subject=Get Started with Easysend"
                className="btn-primary text-base"
                data-testid="hero-cta-get-started"
              >
                Get Started
              </a>
              <a
                href="mailto:payments@easysendservices.com?subject=Contact Easysend"
                className="btn-secondary text-base"
                data-testid="hero-cta-contact"
              >
                Contact
              </a>
            </motion.div>
          </div>

          {/* Right Content - Banner Image - MacBook dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative lg:absolute lg:right-0 lg:top-20 lg:w-[55%] xl:w-[52%] 2xl:w-[50%]"
            data-testid="hero-banner-image"
          >
            <div className="relative overflow-hidden">
              <img 
                src={BANNER_IMAGE}
                alt="Easysend Dashboard - Payment Infrastructure Platform"
                className="w-full h-auto"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
